<template>
  <div class="workflow-runner">
    <div v-if="!showResults" class="workflow-selection">
      <h3>Analyze Document</h3>
      <div class="workflow-grid">
        <div 
          v-for="workflow in workflows" 
          :key="workflow.id"
          class="workflow-card"
          :class="{ selected: selectedWorkflow?.id === workflow.id }"
          @click="selectWorkflow(workflow)"
        >
          <div class="workflow-header">
            <h4>{{ workflow.name }}</h4>
            <span class="provider-badge" :class="workflow.llm_provider.toLowerCase()">
              {{ workflow.get_llm_provider_display }}
            </span>
          </div>
          <p class="description">{{ workflow.description }}</p>
          <div class="workflow-prompts">
            <strong>Analysis Steps:</strong>
            <ul>
              <li v-for="(prompt, index) in workflow.analysis_prompts" 
                  :key="index">{{ prompt }}</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="workflow-actions">
        <button 
          class="run-button"
          @click="runWorkflow"
          :disabled="!canRunWorkflow || loading"
        >
          {{ loading ? 'Running Analysis...' : 'Run Analysis' }}
        </button>
      </div>
    </div>

    <div v-else class="workflow-results">
      <div class="results-header">
        <h3>Analysis Results</h3>
        <button class="back-button" @click="showResults = false">
          ← Back to Workflows
        </button>
      </div>

      <div v-if="results" class="results-content">
        <div class="result-section">
          <h4>Summary</h4>
          <p>{{ results.summary }}</p>
        </div>

        <div class="result-section">
          <h4>Key Obligations</h4>
          <ul>
            <li v-for="(obligation, index) in results.key_obligations" 
                :key="index">{{ obligation }}</li>
          </ul>
        </div>

        <div class="result-section">
          <h4>Risks Identified</h4>
          <ul>
            <li v-for="(risk, index) in results.risks" 
                :key="index">{{ risk }}</li>
          </ul>
        </div>

        <div class="result-section">
          <h4>Insurance Provisions</h4>
          <div class="subsection">
            <h5>Coverage Requirements</h5>
            <ul>
              <li v-for="(req, index) in results.insurance_provisions.coverage_requirements" 
                  :key="index">{{ req }}</li>
            </ul>
          </div>
          <div class="subsection">
            <h5>Coverage Gaps</h5>
            <ul>
              <li v-for="(gap, index) in results.insurance_provisions.gaps" 
                  :key="index">{{ gap }}</li>
            </ul>
          </div>
        </div>

        <div class="result-section">
          <h4>Compliance Issues</h4>
          <ul>
            <li v-for="(issue, index) in results.compliance_issues" 
                :key="index">{{ issue }}</li>
          </ul>
        </div>

        <div class="result-section">
          <h4>Recommendations</h4>
          <ul>
            <li v-for="(rec, index) in results.recommendations" 
                :key="index">{{ rec }}</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'WorkflowRunner',
  props: {
    document: {
      type: Object,
      required: true
    },
    client: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      workflows: [],
      selectedWorkflow: null,
      showResults: false,
      loading: false,
      error: null,
      results: null
    }
  },
  computed: {
    canRunWorkflow() {
      return this.selectedWorkflow && this.document && this.client
    }
  },
  methods: {
    async fetchWorkflows() {
      try {
        const response = await api.get('/workflow-templates/');
        this.workflows = response.data || [];
      } catch (error) {
        this.error = error.message;
      }
    },
    selectWorkflow(workflow) {
      this.selectedWorkflow = workflow;
    },
    async runWorkflow() {
      this.loading = true;
      this.error = null;
      
      try {
        const apiUrl = process.env.VUE_APP_API_URL || '/api';
        const baseUrl = apiUrl.startsWith('http') ? apiUrl : window.location.origin + apiUrl;
        const response = await fetch(`${baseUrl}/workflows/run/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            workflow_id: this.selectedWorkflow.id,
            document_id: this.document.id,
            client_id: this.client.id
          })
        });

        if (!response.ok) throw new Error('Failed to run workflow');
        
        const data = await response.json();
        this.results = data.results;
        this.showResults = true;
      } catch (error) {
        this.error = error.message;
      } finally {
        this.loading = false;
      }
    }
  },
  mounted() {
    this.fetchWorkflows();
  }
}
</script>

<style scoped>
.workflow-runner {
  padding: 1rem;
}

/* ... existing workflow-grid and workflow-card styles ... */

.workflow-prompts {
  margin-top: 1rem;
  font-size: 0.9rem;
}

.workflow-prompts ul {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.workflow-prompts li {
  color: var(--color-dark);
  margin: 0.25rem 0;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.back-button {
  background: none;
  border: none;
  color: var(--color-primary);
  cursor: pointer;
  font-size: 1rem;
}

.result-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.result-section h4 {
  color: var(--color-primary);
  margin-top: 0;
  margin-bottom: 1rem;
}

.subsection {
  margin-top: 1rem;
}

.subsection h5 {
  color: var(--color-secondary);
  margin-bottom: 0.5rem;
}

ul {
  margin: 0;
  padding-left: 1.5rem;
}

li {
  margin: 0.5rem 0;
  line-height: 1.4;
}
</style> 